/**
 * What to send up, what to bring down, and when to stop and ask.
 *
 * Pure on purpose. This is the piece that can lose somebody's collection, so
 * it takes two plain snapshots and returns a plan, with no storage, no network
 * and no React anywhere near it — which means every case it can meet is a test
 * rather than a thing to find out about later.
 */

/** Where this device last synced to, so a first sign-in can be recognised. */
export const ACCOUNT_KEY = "ppt_sync_account_v1";

const at = (iso) => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/**
 * @param local  { entries, stamps } from this browser
 * @param server { entries, stamps } from the account
 * @param known  true if this device has synced with this account before
 *
 * @returns {{ apply, applyStamps, push, conflicts, ask }}
 *   apply/applyStamps  write these into local storage
 *   push               send these up
 *   conflicts          keys where both sides changed independently
 *   ask                true when a person has to choose, not the code
 */
export function reconcile(local, server, known) {
  const apply = {};
  const applyStamps = {};
  const push = { entries: {}, stamps: {} };
  const conflicts = [];

  const keys = new Set([
    ...Object.keys(local.entries || {}),
    ...Object.keys(server.entries || {}),
  ]);

  for (const key of keys) {
    const mine = local.entries?.[key];
    const theirs = server.entries?.[key];
    const mineAt = at(local.stamps?.[key]);
    const theirsAt = at(server.stamps?.[key]);

    if (mine !== undefined && theirs === undefined) {
      /* The account has never held this. Undated local data still goes up —
         it is the only copy, and refusing to send it because nobody wrote
         down when it changed would be losing it to a technicality. */
      push.entries[key] = mine;
      push.stamps[key] = local.stamps?.[key] || new Date(0).toISOString();
      continue;
    }

    if (mine === undefined && theirs !== undefined) {
      apply[key] = theirs;
      applyStamps[key] = server.stamps[key];
      continue;
    }

    if (mine === theirs) continue;      // same content, nothing to settle

    /* Both sides have it and they differ. Dates decide — but only once this
       device and this account share a history. Before that the two dates come
       from unrelated lineages, and "newer" is a coincidence of when somebody
       happened to open which browser, not evidence of what they want kept. */
    if (!known) {
      conflicts.push(key);
      continue;
    }

    if (mineAt !== null && (theirsAt === null || mineAt > theirsAt)) {
      push.entries[key] = mine;
      push.stamps[key] = local.stamps[key];
    } else if (theirsAt !== null && (mineAt === null || theirsAt > mineAt)) {
      apply[key] = theirs;
      applyStamps[key] = server.stamps[key];
    }
    /* Equal dates with different content is the one genuinely unanswerable
       case. Left alone rather than guessed at: whichever side is edited next
       will carry a later date and settle it honestly. */
  }

  return {
    apply,
    applyStamps,
    push,
    conflicts,
    ask: conflicts.length > 0,
  };
}

/** Everything on one side, for the "keep this / keep that" choice. */
export function takeAll(from) {
  return {
    entries: { ...(from.entries || {}) },
    stamps: { ...(from.stamps || {}) },
  };
}

/**
 * Put the account's copy away when somebody signs out.
 *
 * Signing out used to leave everything sitting in the browser, which is fine on
 * your own laptop and not fine anywhere else. Two things went wrong on a shared
 * one: the next person saw the whole collection, and — worse — if they signed
 * into their own account, the leftovers became their "two collections" prompt.
 * Pressing the obvious button then moved somebody else's cards into their
 * account, through a dialog that looked entirely reasonable to them.
 *
 * Only what an account was carrying. Someone who has never signed in has no
 * marker, so nothing here touches them, and anyone signing out is leaving a
 * copy on the server that signing back in returns in full.
 *
 * @param keys the storage keys an account syncs
 * @returns true if there was anything to put away
 */
export function forgetAccountCopy(keys, stampsKey, store) {
  if (!store.getItem(ACCOUNT_KEY)) return false;

  for (const key of keys) store.removeItem(key);

  /* The dates go with the values. Left behind, they would say this browser
     had changed things it no longer has, and the next sync would read that as
     a deliberate deletion and clear the account too. */
  try {
    const stamps = JSON.parse(store.getItem(stampsKey) || "{}");
    for (const key of keys) delete stamps[key];
    store.setItem(stampsKey, JSON.stringify(stamps));
  } catch {
    store.removeItem(stampsKey);
  }

  store.removeItem(ACCOUNT_KEY);
  return true;
}
